import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { format } from 'date-fns';
import { Dropdown } from '../Dropdown';
import { Stack } from '@contrast-security-inc/design-system-layouts';
import { Popover, Tooltip } from '@contrast-security-inc/design-system-popovers';
import { DateRangePicker } from '../DateRangePicker';
import { Text } from '@contrast-security-inc/design-system-typography';
import { Button } from '@contrast-security-inc/design-system-buttons';
import { useLocalizationContext } from '@contrast-security-inc/design-system-foundations';
import * as messages from './translations';
import { highlightedTextStyles } from '../SelectModules';
export const DateRangeDropdown = ({ customDateFormat, customTimeFormat, inputHeight = 65, items, label, onChangeRange, selectedRange, minDate, maxDate, showTime = true, testId, wrapCustomDateRange = true, ...rest }) => {
    const { formatMessage, formatDate, formatTime } = useLocalizationContext();
    const [startDate, setStartDate] = useState(selectedRange.startDate);
    const [endDate, setEndDate] = useState(selectedRange.endDate);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRangePickerProps, setDateRangePickerProps] = useState(undefined);
    const dropdownRef = useRef(null);
    useEffect(() => {
        if (selectedRange) {
            setStartDate(selectedRange.startDate);
            setEndDate(selectedRange.endDate);
        }
    }, [selectedRange]);
    const formatDateTime = (date) => {
        const formattedDate = customDateFormat ? format(date, customDateFormat) : formatDate(date);
        const formattedTime = customTimeFormat ? format(date, customTimeFormat) : formatTime(date);
        return `${formattedDate} ${showTime ? formattedTime : ''}`;
    };
    const renderInputValue = (selectedRange, isOpen) => {
        const formattedStartDate = formatDateTime(selectedRange.startDate);
        const formattedEndDate = formatDateTime(selectedRange.endDate);
        return !isOpen && selectedRange.type !== 'CUSTOM_DATE_RANGE' ? (React.createElement(Tooltip, { body: `${formattedStartDate} - ${formattedEndDate}` },
            React.createElement(Text, { testId: `${testId}-input-value`, as: "div", font: "bodyStrong" }, getItemLabel(selectedRange)))) : (React.createElement(Stack, { flexDirection: wrapCustomDateRange ? 'column' : 'row', spacing: "0px", testId: `${testId}-selected-item-input-value-stack` },
            React.createElement(Stack, { spacing: "0px", flexDirection: "row" },
                React.createElement(Text, { testId: `${testId}-input-value-startDate`, as: "div", font: "bodyStrong" }, formattedStartDate),
                "\u00A0\u2013\u00A0"),
            React.createElement(Text, { testId: `${testId}-input-value-endDate`, as: "div", font: "bodyStrong" }, formattedEndDate)));
    };
    function getItemLabel(item) {
        if (item.type === 'CUSTOM_DATE_RANGE') {
            return item.label ?? formatMessage(messages, 'title_customDate');
        }
        switch (item.unit) {
            case 'MINUTES':
                return formatMessage(messages, 'detail_nMinutes', { count: item.count });
            case 'HOURS':
                return formatMessage(messages, 'detail_nHours', { count: item.count });
            case 'DAYS':
                return formatMessage(messages, 'detail_nDays', { count: item.count });
            case 'WEEKS':
                return formatMessage(messages, 'detail_nWeeks', { count: item.count });
            case 'MONTHS':
                return formatMessage(messages, 'detail_nMonths', { count: item.count });
            case 'YEARS':
                return formatMessage(messages, 'detail_nYears', { count: item.count });
            default:
                return '';
        }
    }
    function closeDatePicker() {
        setShowDatePicker(false);
    }
    function getItemId(item) {
        if (item.type === 'RELATIVE_TO_NOW') {
            return `${item.type} ${item.count} ${item.unit}`;
        }
        else {
            return item.label ?? item.type;
        }
    }
    function renderCustomItem(item) {
        let isSelected = false;
        if (item.type === 'CUSTOM_DATE_RANGE') {
            const selectedItem = selectedRange;
            isSelected = item.type === selectedItem.type;
        }
        else if (item.type === 'RELATIVE_TO_NOW') {
            const selectedItem = selectedRange;
            isSelected = item.count === selectedItem.count && item.unit === selectedItem.unit;
        }
        return React.createElement("span", { className: isSelected ? highlightedTextStyles : '' }, getItemLabel(item));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Dropdown, { ...rest, testId: testId, ref: dropdownRef, value: { type: 'RELATIVE_TO_NOW', count: -1, unit: 'DAYS' }, itemToID: getItemId, itemToString: getItemLabel, customRender: renderCustomItem, renderInputValue: (_, isOpen) => {
                return renderInputValue(selectedRange, isOpen);
            }, items: items, label: label, onChange: (_, selectedItem) => {
                if (selectedItem) {
                    const isCustom = selectedItem.type === 'CUSTOM_DATE_RANGE';
                    if (isCustom) {
                        setShowDatePicker(true);
                        setDateRangePickerProps(selectedItem.props);
                        setStartDate(selectedRange.startDate);
                        setEndDate(selectedRange.endDate);
                    }
                    else if (selectedItem.type === 'RELATIVE_TO_NOW') {
                        const dateRange = getRelativeRange(selectedItem);
                        onChangeRange(dateRange);
                    }
                }
            }, inputWrapperSpanClassName: css `
          ${wrapCustomDateRange && `height: ${inputHeight}px;`}
        `, onClick: closeDatePicker, isDisabled: showDatePicker, showOverflowTooltip: false }),
        React.createElement(Popover, { testId: `${testId}-custom-popover`, referenceElement: dropdownRef?.current, placement: "bottom-start", isOpen: showDatePicker, onClose: closeDatePicker },
            React.createElement(DateRangePicker, { startDate: startDate, endDate: endDate, minDate: minDate, maxDate: maxDate ?? 'NOW', monthsToDisplay: 2, onStartDateSelected: (startDate) => {
                    setStartDate(startDate);
                }, onEndDateSelected: (endDate) => {
                    setEndDate(endDate);
                }, ...dateRangePickerProps }),
            React.createElement("span", { className: css `
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            gap: 16px;
            margin-bottom: 24px;
            margin-right: 24px;
          ` },
                React.createElement(Button, { variant: "secondary", onClick: closeDatePicker }, formatMessage(messages, 'action_cancel')),
                React.createElement(Button, { isDisabled: !startDate || !endDate, variant: "primary", onClick: () => {
                        if (startDate && endDate) {
                            onChangeRange({
                                type: 'CUSTOM_DATE_RANGE',
                                startDate,
                                endDate,
                            });
                            closeDatePicker();
                        }
                    } }, formatMessage(messages, 'action_apply'))))));
};
export function getRelativeRange(item) {
    const endDate = new Date();
    const startDate = new Date(endDate);
    switch (item.unit) {
        case 'MINUTES':
            startDate.setMinutes(endDate.getMinutes() - item.count);
            break;
        case 'HOURS':
            startDate.setHours(endDate.getHours() - item.count);
            break;
        case 'DAYS':
            startDate.setDate(endDate.getDate() - item.count);
            break;
        case 'WEEKS':
            startDate.setDate(endDate.getDate() - item.count * 7);
            break;
        case 'MONTHS':
            startDate.setMonth(endDate.getMonth() - item.count);
            break;
        case 'YEARS':
            startDate.setFullYear(endDate.getFullYear() - item.count);
            break;
    }
    return {
        startDate,
        endDate,
        ...item,
    };
}
